import { BackIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Divider, Stack } from '@mui/material';
import {
  DateCalendar,
  DateTimeField,
  LocalizationProvider,
  MultiSectionDigitalClock,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useEffect, useState } from 'react';
import { DateFilterValue, FilterComponent } from './types';

export const DateFilter: FilterComponent<DateFilterValue[]> = ({
  title,
  filter,
  onApply,
  onBack,
}) => {
  if (filter.type !== 'date') {
    throw new Error('DateFilter does not support this filter type');
  }
  const { value } = filter;

  const [from, setFrom] = useState<Date>(() => {
    if (!value?.length) return null;
    const date = value.find((dateFilter) => dateFilter?.gte);
    return date?.gte;
  });
  const [to, setTo] = useState<Date>(() => {
    if (!value?.length) return null;
    const date = value.find((dateFilter) => dateFilter?.lte);
    return date?.lte;
  });
  const [fromDirty, setFromDirty] = useState(false);
  const [toDirty, setToDirty] = useState(false);
  const [active, setActive] = useState<'from' | 'to'>('from');
  useEffect(() => {
    if (!value?.length) return;
    const gteDate = value.find((dateFilter) => dateFilter?.gte);
    const lteDate = value.find((dateFilter) => dateFilter?.lte);
    if (gteDate) {
      setFrom(gteDate.gte);
    }
    if (lteDate) {
      setTo(lteDate.lte);
    }
  }, [value]);
  const updateDate = (newDate: Date) => {
    if (active === 'from') {
      setFrom(newDate);
      setFromDirty(true);
    } else {
      setTo(newDate);
      setToDirty(true);
    }
  };
  return (
    <Stack
      onKeyDown={(e) => {
        if (e.key === 'Tab') e.stopPropagation();
      }}
    >
      <Group alignItems="center" gap={1} sx={{ px: 2 }}>
        <IconButton size={16} onClick={onBack} Icon={BackIcon} />
        <Typography variant="body2">{title}</Typography>
      </Group>
      <Divider sx={{ m: 2 }} flexItem />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Group gap={2} alignItems="center" sx={{ px: 4 }}>
          <DateTimeField
            clearable
            onClear={() => setFrom(null)}
            focused={active === 'from'}
            value={from}
            onChange={updateDate}
            onFocus={() => setActive('from')}
            inputProps={{
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: 'false',
            }}
            sx={{
              flex: 1,
              minWidth: '212px',
              '& .MuiInputBase-input': { pl: 3, pr: 0 },
              '& .MuiOutlinedInput-root': { border: 'none' },
            }}
          />
          -
          <DateTimeField
            clearable
            onClear={() => setTo(null)}
            focused={active === 'to'}
            value={to}
            onChange={updateDate}
            onFocus={() => setActive('to')}
            inputProps={{
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: 'false',
            }}
            sx={{
              flex: 1,
              minWidth: '212px',
              '& .MuiInputBase-input': { pl: 3, pr: 2 },
              '& .MuiOutlinedInput-root': { border: 'none' },
            }}
          />
        </Group>
        <Group>
          <DateCalendar value={active === 'from' ? from : to} onChange={updateDate} />
          <MultiSectionDigitalClock
            sx={{
              width: 'auto',
              pt: 4,
              '& .MuiList-root': {
                maxHeight: '100%',
                scrollbarGutter: 'stable',
                width: 'auto',
                '&:after': {
                  height: 0,
                },
              },
              '& .MuiList-root-MuiMultiSectionDigitalClockSection-root::after': {
                height: 0,
              },
            }}
            value={active === 'from' ? from : to}
            onChange={updateDate}
          />
        </Group>
      </LocalizationProvider>
      <Divider sx={{ m: 2 }} flexItem />
      <Group justifyContent="flex-end" sx={{ px: 2, gap: 2 }}>
        <Button
          size="small"
          variant="secondary"
          onClick={() => {
            if (from) {
              setFrom(null);
              setFromDirty(true);
            }
            if (to) {
              setTo(null);
              setToDirty(true);
            }
          }}
        >
          Clear all
        </Button>
        <Button
          size="small"
          variant="primary"
          onClick={() => {
            if (!fromDirty && !toDirty && onBack) {
              onBack();
              return;
            }
            const newDates: DateFilterValue[] = [];
            if (from) newDates.push({ gte: from });
            if (to) newDates.push({ lte: to });
            onApply({ value: newDates });
          }}
        >
          Apply
        </Button>
      </Group>
    </Stack>
  );
};
